import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components';
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form'

import ClientLayout from 'spa/client/layouts/ClientLayout'
import { mixinTitle, mixinMainContainer } from 'spa/client/styles/mixin'
import { ExtensionSurveyAnswerItem, UpdateClientContractExtensionRequest } from 'javascripts/api/generated'
import { TextAreaForm } from 'spa/client/components/TextAreaForm'
import { SelectForm } from 'spa/client/components/SelectForm'
import { RadioForm } from 'spa/client/components/RadioForm'
import { Button } from 'spa/client/components/Button'

type Props = {
  description: string,
  userFullName: string,
  qaItems: ExtensionSurveyAnswerItem[],
  onSubmit: SubmitHandler<UpdateClientContractExtensionRequest>,
}

const StyledTitle = styled.h1`
  ${mixinTitle}
`

const StyledMainContainer = styled.div`
  ${mixinMainContainer}
  padding-right: 20px;
  padding-left: 20px;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
  font-size: .875rem;
`

const StyledTr = styled.tr`
  border: 1px solid #EAEDEE;
`

const StyledTh = styled.th`
  padding: .5rem;
  text-align: left;
  background: #F9F9FA;
  font-weight: normal;
  width: 20%;
`

const StyledTd = styled.td`
  padding: .5rem;
  text-align: left;
`

const StyledForm = styled.form`
  width: 100%;
  font-size: .92rem;
`

const StyledUl = styled.ul`
  list-style: none;
  padding-inline-start: 0;
`

const StyledListItem = styled.li`
  margin-bottom: 1.8rem;
`

const StyledQuestion = styled.div`
  display: flex;
  vertical-align: middle;
`

const StyledRequired = styled.span<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  min-width: 40px;
  height: 28px;
  font-size: .75rem;
  color: ${props => props.theme.clients.colors.orange._50};
  font-weight: bold;
  border: 1px solid ${props => props.theme.clients.colors.orange._100};
  border-radius: 4px;
  padding: 7px;
  justify-content: center;
  margin-right: .5rem;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
    `}
`

const StyledOptional = styled.span<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  min-width: 40px;
  height: 28px;
  font-size: .75rem;
  color: ${props => props.theme.clients.colors.silver._100};
  font-weight: bold;
  border: 1px solid ${props => props.theme.clients.colors.silver._100};
  border-radius: 4px;
  padding: 7px;
  justify-content: center;
  margin-right: .5rem;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
    `}
`

const StyledQuestionText = styled.div<{ disabled: boolean }>`
  margin: auto auto auto 0;
  font-weight: bold;
  font-size: 16px;
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
    `}
`

const StyledAnswer = styled.div`
  margin-bottom: 1.83rem;
  width: 100%;
  max-width: 640px;
`

const StyledTerm = styled.div`
  text-align: start;
  margin: auto auto 2em;
`

const StyledTermText = styled.div`
  color: ${props => props.theme.clients.colors.black._60};
  font-size: 14px;
  font-weight: normal;
  margin: 2rem auto 2rem 0;
`

const StyledCheckBox = styled.label`
  display: flex;
  align-items: center;
  margin: 1rem auto 1rem 0;
  background-color: #F9F9FA;
  padding: 1.25rem 0 2rem 1.5rem;
  border: 1px solid #EAEDEE;
`

const StyledCheckBoxInput = styled.input`
  margin-right: .5rem;
`

const StyledButtonContainer = styled.div`
  margin-bottom: calc(2rem + 5.625rem); // 余白とfooterの高さを足し合わせfooterにかからないようにしています
`

const WarningStyledTr = styled.tr`
  border: 2px solid #D52020;
`

const WarningStyledTd = styled.td`
  padding: 15px;
  text-align: left;
  color: #D52020;
  font-weight: bold;
`

const StyledAgreeText = styled.div`
  font-weight: bold;
  font-size: 16px;
`

const StyledAsteriskText = styled.div`
  font-weight: 300;
  white-space: pre-line;
  font-size: 14px;
  text-indent: -0.5em;
  padding-left: 0.5em;
`;

const StyledLink = styled.a`
  color: ${props => props.theme.clients.colors.appleGreen._90};
`;

const StyledQuestionFirstText = styled.div`
  line-height: 28px;
`;

export const StyledClientContractExtensionRequests: React.FC<Props> = (props: Props) => {
  const methods = useForm<UpdateClientContractExtensionRequest>(
    {
      defaultValues:
      {
        contractExtensionRequest:
        {
          extensionSurveyAnswers: {},
          outsourcingChecked: 0,
        }
      }
    }
  )
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [requiredFields, setRequiredFields] = useState<string[]>([]);

  useEffect(() => {
    initializeForm();
  }, [methods]);

  const initializeForm = () => {
    const initialAnswer = methods.getValues('contractExtensionRequest.extensionSurveyAnswers')['1']['answer'];
    setSelectedPeriod(initialAnswer);
    updateRequiredFields(initialAnswer);
    checkRequiredFields();
  };

  const resetFields = (fields: string[]) => {
    fields.forEach(field => {
      methods.setValue(`contractExtensionRequest.extensionSurveyAnswers.${field}.answer`, '');
    });
  };

  const handlePeriodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const displayFlag = methods.getValues(`contractExtensionRequest.extensionSurveyAnswers.displayFlag`);
    if (String(displayFlag) === 'false') {
      return;
    }

    const { name, value } = event.target;
    const questionId = name.split('.')[2]; // 質問IDを取得

    if (questionId === '1') {
      setSelectedPeriod(value);
      updateRequiredFields(value);

      if (value.includes('希望しない')) {
        resetFields(['2', '3', '4']);
      } else if (value.includes('希望する')) {
        resetFields(['5', '6']);
      }
    }
    checkRequiredFields();
  };

  const updateRequiredFields = (answer: string) => {
    if (answer.includes('希望しない')) {
      setRequiredFields(['1', '5', '6', '7', '8']);
    } else if (answer.includes('希望する')) {
      setRequiredFields(['1', '2', '4', '7', '8']);
    } else {
      setRequiredFields(['1', '7', '8']);
    }
  };

  const isRequired = (question: string, required: boolean, display_flag: boolean): boolean => {
    if (display_flag) {
      return !isDisabled(question, display_flag) && required
    } else {
      return required
    }
  }

  const isDisabled = (question: string, display_flag: boolean): boolean => {
    if (display_flag) {
      return (
        (selectedPeriod.includes('希望しない') && question.includes('＜更新を希望する場合＞')) ||
        (selectedPeriod.includes('希望する') && question.includes('＜更新を希望しない場合＞'))
      )
    } else {
      return false
    }
  }

  const checkRequiredFields = () => {
    const allFieldsFilled = requiredFields.every(field => {
      const answer = methods.getValues(`contractExtensionRequest.extensionSurveyAnswers.${field}.answer`);
      return answer && answer.trim() !== '';
    });
    setIsSubmitDisabled(!allFieldsFilled);
  }

  const containsAsterisk = (question: string): boolean => {
    return question.includes('\n');
  };

  const splitQuestion = (question: string): [string, string] => {
    const parts = question.split('\n');
    return [parts[0], parts.slice(1).join('\n')];
  };

  return (
    <ClientLayout>
      <StyledTitle>契約更新の確認</StyledTitle>
      <StyledMainContainer>
        <StyledTable>
          <tbody>
            <WarningStyledTr>
              <WarningStyledTd>
                ※基本的にはご回答いただきました内容で手続きを進めさせていただきます。<br />
                ※ご回答後の内容変更に関しましては速やかにご連絡ください。
              </WarningStyledTd>
            </WarningStyledTr>
          </tbody>
        </StyledTable>
        <StyledTable>
          <tbody>
            <StyledTr>
              <StyledTh>業務内容</StyledTh>
              <StyledTd>{props.description}</StyledTd>
            </StyledTr>
            <StyledTr>
              <StyledTh>業務担当者</StyledTh>
              <StyledTd>{props.userFullName}</StyledTd>
            </StyledTr>
          </tbody>
        </StyledTable>
        <FormProvider {...methods} >
          { }
          <StyledForm onSubmit={methods.handleSubmit(props.onSubmit)}>
            <StyledUl>
              {props.qaItems.map((qa: ExtensionSurveyAnswerItem, index) => (
                <StyledListItem key={index + 1}>
                  <input {...methods.register(`contractExtensionRequest.extensionSurveyAnswers.${(index + 1).toString()}.id`)} defaultValue={qa.id} hidden />
                  <input {...methods.register(`contractExtensionRequest.extensionSurveyAnswers.${(index + 1).toString()}.questionId`)} defaultValue={qa.extensionSurveyQuestion.id} hidden />
                  <input {...methods.register(`contractExtensionRequest.extensionSurveyAnswers.displayFlag`)} defaultValue={qa.extensionSurveyQuestion.displayFlag.toString()} hidden />
                  <StyledQuestion>
                    {qa.extensionSurveyQuestion.required ?
                      <StyledRequired disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}>
                        必須
                      </StyledRequired> :
                      <StyledOptional disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}>
                        任意
                      </StyledOptional>}
                    {containsAsterisk(qa.extensionSurveyQuestion.question) ? (
                      <>
                        <StyledQuestionText disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}>
                          <StyledQuestionFirstText>
                            {splitQuestion(qa.extensionSurveyQuestion.question)[0]}
                          </StyledQuestionFirstText>
                          <StyledAsteriskText disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}>
                            {splitQuestion(qa.extensionSurveyQuestion.question)[1]}
                          </StyledAsteriskText>
                        </StyledQuestionText>
                      </>
                    ) : (
                      <StyledQuestionText disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}>
                        {qa.extensionSurveyQuestion.question}
                      </StyledQuestionText>
                    )}
                  </StyledQuestion>
                  <StyledAnswer onChange={handlePeriodChange}>
                    {qa.extensionSurveyQuestion.format === 'text' && (
                      <TextAreaForm
                        name={`contractExtensionRequest.extensionSurveyAnswers.${(index + 1).toString()}.answer`}
                        defaultValue={qa.answer}
                        required={isRequired(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.required, qa.extensionSurveyQuestion.displayFlag)}
                        disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}
                      />
                    )}
                    {qa.extensionSurveyQuestion.format === 'select' && (
                      <SelectForm
                        name={`contractExtensionRequest.extensionSurveyAnswers.${(index + 1).toString()}.answer`}
                        defaultValue={qa.answer}
                        options={qa.extensionSurveyQuestion.optionsForSelect}
                        required={isRequired(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.required, qa.extensionSurveyQuestion.displayFlag)}
                        disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}
                        placeholder='選択してください'
                      />
                    )}
                    {qa.extensionSurveyQuestion.format === 'radio' && (
                      <RadioForm
                        name={`contractExtensionRequest.extensionSurveyAnswers.${(index + 1).toString()}.answer`}
                        defaultValue={qa.answer}
                        options={qa.extensionSurveyQuestion.optionsForSelect}
                        required={isRequired(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.required, qa.extensionSurveyQuestion.displayFlag)}
                        disabled={isDisabled(qa.extensionSurveyQuestion.question, qa.extensionSurveyQuestion.displayFlag)}
                      />
                    )}
                  </StyledAnswer>
                </StyledListItem>
              ))}
            </StyledUl>
            <StyledTerm>
            {/* TODO: メールアドレスを環境変数から取得するようにしたい */}
            <StyledTermText>
              ※延長のご契約締結には業務委託契約適正化チェックの内容同意が必須となります。
              <StyledLink href='https://drive.google.com/file/d/1qiJ0rVzRdYIY-0RdyrYJHQaPhueFK88I/view' target='_blank' rel='noopener noreferrer'>
                業務委託契約適正化チェックリスト
              </StyledLink>
              <br />
              ※延長を希望されない場合でも、貴社と継続してお取引させていただくために確認のご協力をお願い申し上げます。<br />
              ※チェックリストの記載項目でご質問等あれば、事務局（work-crowdtech@crowdtech.jp）までお問い合わせください。<br />
            </StyledTermText>
            <StyledCheckBox>
              <StyledRequired>必須</StyledRequired>
              <StyledCheckBoxInput type='checkbox' required={true} {...methods.register('contractExtensionRequest.outsourcingChecked')} />
              <StyledAgreeText>業務委託チェックリストの内容実施が問題ない旨を同意する</StyledAgreeText>
            </StyledCheckBox>
          </StyledTerm>
          <StyledButtonContainer>
            <Button type='submit' buttonType='SECONDARY' label='送信する' disabled={isSubmitDisabled}/>
          </StyledButtonContainer>
          </StyledForm>
        </FormProvider>
      </StyledMainContainer>
    </ClientLayout>
  )
}
